import type { ConsentDefinition } from '@forma/shared';
import { moveConsentLinkToText } from './richTextEditing';

type MergeableConsent = Omit<ConsentDefinition, 'id'> & { id?: string };

/**
 * «Я ознакомился с [A]» + «Я ознакомился с [B]» → «Я ознакомился с [A] и [B]».
 * Если начало фраз не совпадает (меньше двух общих слов), тексты идут с новой строки.
 */
function joinTexts(a: string, b: string) {
  if (!a || !b) return a || b;
  const wa = a.split(' '); const wb = b.split(' ');
  let common = 0;
  while (common < wa.length - 1 && common < wb.length - 1 && wa[common] === wb[common] && !wa[common].includes('[')) common++;
  if (common < 2) return `${a}\n${b}`;
  // Знаки препинания на стыке («…[A]:» + «: , [B]») не дублируются.
  const restA = wa.slice(common).join(' ').replace(/[\s.;:,]+$/, '');
  const restB = wb.slice(common).join(' ').replace(/^[\s.;:,]+/, '');
  if (!restA || !restB) return `${a}\n${b}`;
  return `${wa.slice(0, common).join(' ')} ${restA} и ${restB}`;
}

/**
 * Объединяет две галочки согласия в одну (например, ознакомление с двумя политиками).
 * Общее начало фраз не повторяется, все ссылки остаются в тексте (их может быть сколько угодно);
 * галочка обязательна, если была обязательной хотя бы одна; окно с полным текстом сохраняется, если оно было у любой из двух.
 */
export function mergeConsents<T extends MergeableConsent>(rawFirst: T, rawSecond: T): T {
  const first = moveConsentLinkToText(rawFirst); const second = moveConsentLinkToText(rawSecond);
  const text = joinTexts(first.text.trim(), second.text.trim());
  const fullText = [first.fullText, second.fullText].map((value) => (value ?? '').trim()).filter(Boolean).join('\n\n');
  const presentation = first.presentation === 'dialog' || second.presentation === 'dialog' ? 'dialog' : 'checkbox';
  return {
    ...first,
    text: text.slice(0, 1000),
    required: Boolean(first.required || second.required),
    linkLabel: '',
    linkUrl: '',
    fullText,
    presentation: presentation && fullText ? presentation : 'checkbox',
    requireReading: Boolean(first.requireReading || second.requireReading)
  };
}
